import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { ChartBar, WarningCircle, ArrowsClockwise, Trash } from '@phosphor-icons/react';
import { PageHeader } from '@/components/enterprise/PageHeader';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/enterprise/EmptyState';
import { devApi } from './devApi';
import { jstDateTime } from '@/utils/jstDate';

const Bar = ({ rows, max, testid, extra }) => (
  <div className="space-y-1.5" data-testid={testid}>
    {rows.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
    {rows.map((r) => (
      <div key={r.key} data-testid={`${testid}-row-${r.key}`}>
        <div className="flex items-baseline justify-between gap-2">
          <p className="min-w-0 truncate text-sm text-zinc-900">{r.label}</p>
          <p className="shrink-0 text-sm font-bold tabular-nums text-zinc-900">
            {r.count}{r.qty ? `件 / ${r.qty}` : '件'}
          </p>
        </div>
        <div className="h-1.5 w-full rounded bg-zinc-200">
          <div className="h-1.5 rounded bg-teal-600"
            style={{ width: `${Math.max((r.count / (max || 1)) * 100, 4)}%` }} />
        </div>
        {extra?.(r) && <p className="break-words text-xs text-zinc-600">{extra(r)}</p>}
      </div>
    ))}
  </div>
);

const Card = ({ title, hint, children, testid }) => (
  <section className="rounded-lg border border-zinc-300 bg-white p-4 shadow-sm" data-testid={testid}>
    <p className="text-sm font-bold text-zinc-900">{title}</p>
    {hint && <p className="mb-2 text-xs text-zinc-600">{hint}</p>}
    <div className="mt-2">{children}</div>
  </section>
);

/** トラブル分析 — どこで落ちているかを全案件から集計する。 */
export default function Insights() {
  const nav = useNavigate();
  const [d, setD] = useState(null);
  const [days, setDays] = useState(365);

  useEffect(() => {
    devApi.insights(days).then(setD).catch(() => toast.error('インサイトを読み込めませんでした'));
  }, [days]);

  if (!d) {
    return <div className="p-6 text-sm text-zinc-600" data-testid="dev-insights-loading">読み込み中…</div>;
  }
  const t = d.totals;
  const mx = (rows) => Math.max(...rows.map((r) => r.count), 1);

  return (
    <div className="space-y-6" data-testid="dev-insights-page">
      <PageHeader title="トラブル分析"
        description="検証・スクラップの記録から、どの部品・図面・原因でつまずいているかを全案件で集計します。"
        testid="dev-insights-header"
        secondary={
          <>
            {[[90, '直近3か月'], [365, '直近1年'], [3650, 'すべて']].map(([v, l]) => (
              <Button key={v} size="sm" variant={days === v ? 'default' : 'outline'}
                onClick={() => setDays(v)} data-testid={`dev-insights-range-${v}`}>{l}</Button>
            ))}
          </>
        }
        menuItems={[
          { label: '全体傾向を開く', onClick: () => nav('/engineering/developments/analytics'),
            testid: 'dev-insights-analytics-btn' },
          { label: 'コスト分析を開く', onClick: () => nav('/engineering/developments/cost'),
            testid: 'dev-insights-cost-btn' },
          { label: '学びのダイジェストを開く', onClick: () => nav('/engineering/developments/learning'),
            testid: 'dev-insights-learning-btn' },
          { label: '試作の準備状況を開く', icon: <ArrowsClockwise size={15} />,
            onClick: () => nav('/engineering/developments/readiness'),
            testid: 'dev-insights-readiness-btn' },
        ]} />

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-5">
        {[['不具合の記録', t.failure_records, 'text-rose-700', 'failures'],
          ['再発', t.recurrences, 'text-rose-700', 'recurrences'],
          ['検証の記録', t.validations, 'text-zinc-900', 'validations'],
          ['スクラップ件数', t.scrap_events, 'text-amber-700', 'scrap'],
          ['スクラップ数量', t.scrap_qty, 'text-amber-700', 'scrapqty']].map(([l, v, tone, k]) => (
          <div key={k} className="rounded-lg border border-zinc-300 bg-white px-4 py-3 shadow-sm"
            data-testid={`dev-insights-tile-${k}`}>
            <p className="text-xs font-semibold text-zinc-600">{l}</p>
            <p className={`text-2xl font-bold tabular-nums ${tone}`}>{v}</p>
          </div>
        ))}
      </div>

      {t.failure_records === 0 && t.scrap_events === 0 ? (
        <EmptyState icon={ChartBar} title="まだ集計できる記録がありません"
          description="検証結果とスクラップの記録が増えると、ここに傾向が表示されます。"
          testid="dev-insights-empty" />
      ) : (
        <div className="grid gap-3 lg:grid-cols-2">
          <Card title="不具合の分類" hint="どんな種類の不具合が多いか"
            testid="dev-insights-categories">
            <Bar rows={d.categories} max={mx(d.categories)} testid="dev-insights-cat"
              extra={(r) => (r.recurrences ? `うち再発 ${r.recurrences}件` : null)} />
          </Card>
          <Card title="落ちている部品" hint="検証で問題になった部品"
            testid="dev-insights-parts">
            <Bar rows={d.failed_parts} max={mx(d.failed_parts)} testid="dev-insights-part"
              extra={(r) => [...(r.categories || []), ...(r.projects || [])].join(' · ') || null} />
          </Card>
          <Card title="落ちている図面 / リビジョン" hint="改訂につながった図面"
            testid="dev-insights-drawings">
            <Bar rows={d.failed_drawings} max={mx(d.failed_drawings)} testid="dev-insights-drawing"
              extra={(r) => (r.parts || []).join('、') || null} />
          </Card>
          <Card title="スクラップの理由" hint="部品を廃棄した理由と数量"
            testid="dev-insights-scrap">
            <Bar rows={d.scrap_reasons} max={mx(d.scrap_reasons)} testid="dev-insights-scrapreason"
              extra={(r) => (r.parts || []).join('、') || null} />
          </Card>
          <Card title="不具合が多い案件" hint="クリックで案件を開きます"
            testid="dev-insights-projects">
            <div className="space-y-1">
              {d.projects.map((p) => (
                <button key={p.key} type="button"
                  onClick={() => nav(`/engineering/developments/${p.development_id}`)}
                  data-testid={`dev-insights-project-${p.development_id}`}
                  className="flex w-full items-center gap-2 rounded px-2 py-1 text-left hover:bg-teal-50">
                  <WarningCircle size={14} className="text-amber-700" />
                  <span className="min-w-0 flex-1 truncate text-sm">{p.label}</span>
                  <span className="text-sm font-bold tabular-nums">{p.count}件</span>
                </button>
              ))}
              {d.projects.length === 0 && <p className="text-sm text-zinc-500">記録はありません。</p>}
            </div>
          </Card>
          <Card title="読み方" testid="dev-insights-help">
            <ul className="list-disc space-y-1 pl-5 text-sm text-zinc-700">
              <li>同じ図面が何度も出てくる場合は、図面そのものを見直す価値があります。</li>
              <li><Trash size={12} className="inline" /> スクラップ理由が「寸法」に偏る場合、加工前の確認手順を追加すると効きます。</li>
              <li>再発が多い分類は、対策が定着していないサインです。</li>
            </ul>
          </Card>
        </div>
      )}
      <p className="text-xs text-zinc-500">作成: {jstDateTime(d.generated_at)}</p>
    </div>
  );
}
